import React, { ReactNode, useCallback, useRef } from 'react';
import styled, { css } from 'astroturf/react';

import {
  Modal as ModalBase,
  ModalContent as ModalContentBase,
  ModalButtonGroup,
} from 'js/design_system/Modal';
import { TextButton } from 'js/design_system/TextButton';
import $image_url from 'js/utils/image_url';
import ImpressionWatcher, {
  Props as ImpressionProps,
} from 'js/utils/impressions/ImpressionWatcher';
import { Button } from 'js/design_system/Button';
import { useBreakpoint } from 'js/design_system/hooks/useBreakpoint';
import useScrollLock from 'js/utils/hooks/useScrollLock';

import { Tag, Image, Paper } from 'js/social/shared/types';
import {
  SocialEditor,
  SocialEditorHandle,
} from 'js/social/shared/SocialEditor/SocialEditor';

const Modal = styled(ModalBase)`
  @import 'stylesheets/design_system/breakpoint';

  overflow-y: hidden;

  &:global([open]) {
    display: flex;
  }

  & > :global(.ds2-5-modal__container) {
    overflow-y: hidden;

    @include breakpoint(medium, large) {
      width: min(800px, calc(100vw - 16px));
      max-height: min(750px, calc(100vh - 16px));
      min-height: auto;
    }

    @include breakpoint(small) {
      border-radius: 0;
      padding-right: 16px;
      padding-left: 16px;
    }
  }

  & > :global(.ds2-5-modal__container) > :global(.ds2-5-modal__header--empty) {
    @include breakpoint(small) {
      margin-bottom: 38px;
    }
  }
`;

const ModalContent = styled(ModalContentBase)`
  // Add some negative margin to prevent the box shadow of buttons being clipped
  margin: -10px;
  padding: 10px;
  gap: 16px;

  overflow-y: hidden;
`;

const CreateItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  border-bottom: 1px solid var(--stroke-light, #dddde2);
  padding-bottom: 24px;
`;

const ReplyWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const ReplyThread = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ThreadLine = styled.div`
  flex: 1;
  border-left: 1px solid var(--stroke-light, #dddde2);
`;

const ReplyToContent = styled.div`
  flex: 1;
  padding-bottom: var(--spacing-sm-24, 24px);
  overflow: hidden;
`;

const ProfileWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  padding-bottom: var(--spacing-sm-32, 32px);
`;

const EditorWrapper = styled.div`
  flex: 1 0;
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const SocialEditorWrapper = styled.div<{ center?: boolean }>`
  display: flex;
  flex: 1;

  &.center {
    padding-top: 10px;
  }
`;

const SocialEditorClass = css`
  @import 'stylesheets/design_system/font';

  @include body__large;
  min-height: 30px;
`;

const ProfileImg = styled.img`
  @import 'stylesheets/design_system/breakpoint';

  width: 48px;
  height: 48px;
  border-radius: 50%;

  @include breakpoint(small) {
    width: 40px;
    height: 40px;
  }
`;

const ContextWrapper = styled.div`
  margin-top: var(--spacing-sm-24, 24px);
  position: relative;
`;

const RemoveWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const RemoveButton = styled(TextButton)`
  border-radius: var(--corner-radius-radius-md, 8px) !important;
  padding: 18px 8px;
  background: rgba(20, 20, 19, 0.7);
`;

const AttachmentsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 32px;
  padding-top: 16px;
`;

const OverflowImpressionWatcher = styled(ImpressionWatcher)`
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
`;

function ImageUploader({ disabled }: { disabled: boolean }) {
  return (
    <TextButton inline icon="image" disabled={disabled}>
      Image
    </TextButton>
  );
}

function PaperUploader({ disabled }: { disabled: boolean }) {
  return (
    <TextButton inline icon="description" disabled={disabled}>
      Paper
    </TextButton>
  );
}

type Props = {
  replyTo?: {
    node: ReactNode;
    profileImg: ReactNode;
  };
  contextNode?: ReactNode;
  placeholder?: string;
  submitButtonText: string;
  submitButtonEnabled: boolean;
  currentUserProfileImgUrl?: string;
  currentUserProfileNode: ReactNode;
  allowAttachments: boolean;

  content: string;
  tags: Tag[];
  image?: Image;
  paper?: Paper;

  onClose(): void;
  onSubmit(): void;
  onChange(content: string | undefined, tags: Tag[]): void;

  impressionPropsGen: (clickTarget?: string) => ImpressionProps;
};

export const CreateSocialItemModal = React.forwardRef<HTMLDialogElement, Props>(
  (
    {
      replyTo,
      contextNode,
      placeholder,
      submitButtonText,
      submitButtonEnabled,
      currentUserProfileImgUrl,
      currentUserProfileNode,
      content,
      tags,
      image,
      paper,
      allowAttachments,
      onClose,
      onSubmit,
      onChange,
      impressionPropsGen,
    },
    ref,
  ) => {
    // The <Modal> component attempts to prevent scrolling when active but this
    // doesn't currently work on the LISWP so we'll prevent it ourselves.
    useScrollLock();

    const socialEditorRef = useRef<SocialEditorHandle>(null);

    const handleEditorChange = useCallback(
      ({ content, tags }: { content: string; tags: Tag[] }) => {
        onChange(content, tags);
      },
      [onChange],
    );

    const focusEditor = useCallback(() => {
      // The interaction of the <dialog> in the <Modal> component doesn't work
      // well with the initial focus() call inside the <SocialEditor>. To get
      // around this, we manually trigger a focus after the modal animation
      // ends.
      if (socialEditorRef.current) {
        socialEditorRef.current.focus();
      }
    }, [socialEditorRef]);

    const handleRemoveAttachment = useCallback(() => {
      // TODO
    }, []);

    const canAttach = allowAttachments && !image && !paper;

    const blankProfileImg = $image_url('s65_no_pic.gif');

    const breakpoint = useBreakpoint();

    return (
      <Modal
        ref={ref}
        open={true}
        onClose={onClose}
        onAnimationEnd={focusEditor}
      >
        <ModalContent>
          <OverflowImpressionWatcher {...impressionPropsGen()}>
            <CreateItemWrapper>
              {replyTo && (
                <ReplyWrapper>
                  <ReplyThread>
                    {replyTo.profileImg}
                    <ThreadLine />
                  </ReplyThread>
                  <ReplyToContent>{replyTo.node}</ReplyToContent>
                </ReplyWrapper>
              )}
              {!replyTo && (
                <ProfileWrapper>
                  <ProfileImg
                    src={currentUserProfileImgUrl || blankProfileImg}
                  />
                  {currentUserProfileNode}
                </ProfileWrapper>
              )}
              <EditorWrapper>
                {replyTo && (
                  <ProfileImg
                    src={currentUserProfileImgUrl || blankProfileImg}
                  />
                )}
                <SocialEditorWrapper center={!!replyTo}>
                  <SocialEditor
                    ref={socialEditorRef}
                    content={content}
                    tags={tags}
                    placeholder={placeholder}
                    disableTags={false}
                    disableHashtags={false}
                    onChange={handleEditorChange}
                    onSubmit={onSubmit}
                    containerClassName={SocialEditorClass}
                  />
                </SocialEditorWrapper>
              </EditorWrapper>
              {/* TODO: add warning for an attached paper or image without any associated text */}
              {contextNode && (
                <ImpressionWatcher {...impressionPropsGen('attachment')}>
                  <ContextWrapper>
                    <RemoveWrapper>
                      <RemoveButton
                        inverse
                        inline
                        icon="close"
                        onClick={handleRemoveAttachment}
                      >
                        Remove
                      </RemoveButton>
                    </RemoveWrapper>
                    {contextNode}
                  </ContextWrapper>
                </ImpressionWatcher>
              )}
              {allowAttachments && (
                <AttachmentsRow>
                  {/* TODO: Actually allow uploading images and papers */}
                  <ImageUploader disabled={!canAttach} />
                  <PaperUploader disabled={!canAttach} />
                </AttachmentsRow>
              )}
            </CreateItemWrapper>
          </OverflowImpressionWatcher>
          <ModalButtonGroup size={breakpoint === 'small' ? 'small' : 'default'}>
            <ImpressionWatcher {...impressionPropsGen('cancel-button')}>
              <TextButton onClick={onClose} style={{ color: '#4A4A4A' }}>
                Cancel
              </TextButton>
            </ImpressionWatcher>
            <ImpressionWatcher {...impressionPropsGen('post-button')}>
              <Button onClick={onSubmit} disabled={!submitButtonEnabled}>
                {submitButtonText}
              </Button>
            </ImpressionWatcher>
          </ModalButtonGroup>
        </ModalContent>
      </Modal>
    );
  },
);
